
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// Fix: Use namespace import to bypass missing exported member errors in react-router-dom
import * as ReactRouterDom from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Briefcase, Plus, ChevronRight, Activity, Download, Search, Filter, SortAsc, Trash2, Edit3 } from 'lucide-react';
import { ProgramFormModal } from '../features/programs/ProgramFormModal';
import { Button } from '../components/ui/Button';
import Papa from 'papaparse';

const { useNavigate } = ReactRouterDom as any;

const ProgramsList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editingProgram, setEditingProgram] = useState<any | null>(null);
  const [isAddProgramOpen, setIsAddProgramOpen] = useState(false);
  
  // Filtering states
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'

  // Sorting states
  const [sortField, setSortField] = useState<'name' | 'category' | 'status' | 'engagements'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const { data: programs, isLoading } = useQuery({
    queryKey: ['programs'],
    queryFn: async () => {
      const { data } = await supabase.from('programs').select('*, engagements(*), program_categories(label)').order('name');
      return data || [];
    }
  });

  // Category lookup for selector
  const { data: categories } = useQuery({
    queryKey: ['program_categories'],
    queryFn: async () => {
      const { data } = await supabase.from('program_categories').select('*');
      return data || [];
    }
  });

  // Inline deletion mutation
  const deleteProgramMutation = useMutation({
    mutationFn: async (programId: string) => {
      const { error } = await supabase.from('programs').delete().eq('id', programId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['programs'] });
    }
  });

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete the program "${name}"? This action cannot be undone.`)) {
      deleteProgramMutation.mutate(id);
    }
  };

  const handleSort = (field: 'name' | 'category' | 'status' | 'engagements') => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIndicator = ({ field }: { field: 'name' | 'category' | 'status' | 'engagements' }) => {
    if (sortField !== field) {
      return <SortAsc size={12} className="ml-1.5 opacity-20 inline-block align-middle" />;
    }
    return sortDirection === 'asc' 
      ? <SortAsc size={12} className="ml-1.5 text-slate-900 inline-block align-middle transition-transform" /> 
      : <SortAsc size={12} className="ml-1.5 text-slate-900 inline-block align-middle rotate-180 transition-transform" />;
  };

  const filteredPrograms = useMemo(() => {
    if (!programs) return [];
    let result = [...programs];

    // Filter by Search Term (matches Name and Description)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(p => 
        p.name.toLowerCase().includes(term) || 
        (p.description && p.description.toLowerCase().includes(term))
      );
    }

    // Filter by Category
    if (categoryFilter !== 'all') {
      result = result.filter(p => p.category_id === categoryFilter);
    }

    // Filter by Status
    if (statusFilter === 'active') {
      result = result.filter(p => p.is_active);
    } else if (statusFilter === 'inactive') {
      result = result.filter(p => !p.is_active);
    }

    // Apply sorting
    result.sort((a, b) => {
      let valA: any = '';
      let valB: any = '';

      if (sortField === 'name') {
        valA = a.name || '';
        valB = b.name || '';
      } else if (sortField === 'category') {
        valA = a.program_categories?.label || '';
        valB = b.program_categories?.label || '';
      } else if (sortField === 'status') {
        valA = a.is_active ? 'Active' : 'Inactive';
        valB = b.is_active ? 'Active' : 'Inactive';
      } else if (sortField === 'engagements') {
        valA = a.engagements?.length || 0;
        valB = b.engagements?.length || 0;
      }

      if (typeof valA === 'string') {
        return sortDirection === 'asc' 
          ? valA.localeCompare(valB) 
          : valB.localeCompare(valA);
      } else {
        return sortDirection === 'asc' 
          ? valA - valB 
          : valB - valA;
      }
    });

    return result;
  }, [programs, searchTerm, categoryFilter, statusFilter, sortField, sortDirection]);

  const handleExport = () => {
    if (!filteredPrograms) return;
    const data = filteredPrograms.map(p => ({
      'Program Name': p.name,
      'Description': p.description || '',
      'Status': p.is_active ? 'Active' : 'Archived',
      'Category': p.program_categories?.label || '—',
      'Engagements Count': p.engagements?.length || 0
    }));
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `hfli_programs_${new Date().toISOString().slice(0, 10)}.csv`);
    link.click();
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20 max-w-7xl mx-auto" id="programs-list-container">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6" id="programs-header-section">
        <div>
          <h1 className="text-3xl font-black text-slate-900 uppercase" id="programs-page-title">
            Programs <span className="text-slate-300 ml-1">({filteredPrograms.length})</span>
          </h1>
          <p className="text-slate-500 font-bold text-xs uppercase tracking-widest mt-1" id="programs-page-subtitle">Manage core initiatives and portfolios</p>
        </div>
        <div className="flex flex-wrap items-center gap-4" id="programs-header-actions">
          <Button id="programs-export-btn" variant="outline" onClick={handleExport} icon={<Download size={18} />}>Export CSV</Button>
          <Button 
            id="programs-create-btn"
            onClick={() => setIsAddProgramOpen(true)}
            icon={<Plus size={18} />}
            className="px-8 py-4"
          >
            New Program
          </Button>
        </div>
      </div>

      {/* Interactive Filtering controls */}
      <div className="bg-white rounded-md shadow-sm border border-slate-200 p-6 grid grid-cols-1 md:grid-cols-3 gap-6" id="programs-filter-panel">
        <div className="relative" id="filter-search-name-group">
          <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5" htmlFor="program-search-input">Search Program Name</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <input
              id="program-search-input"
              type="text"
              placeholder="Search by name or description..."
              className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-[11px] outline-none focus:ring-4 focus:ring-slate-100 transition-all shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div id="filter-category-group">
          <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5" htmlFor="program-category-select">Filter Category</label>
          <select
            id="program-category-select"
            className="w-full px-3 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-[11px] outline-none focus:ring-4 focus:ring-slate-100 transition-all shadow-sm"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">All Categories</option>
            {categories?.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.label}</option>
            ))}
          </select>
        </div>

        <div id="filter-status-group">
          <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5" htmlFor="program-status-select">Filter Status</label>
          <select
            id="program-status-select"
            className="w-full px-3 py-2 bg-white border border-slate-200 rounded font-bold text-slate-900 text-[11px] outline-none focus:ring-4 focus:ring-slate-100 transition-all shadow-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active Only</option>
            <option value="inactive">Archived / Inactive Only</option>
          </select>
        </div>
      </div>

      {/* Program list interactive table */}
      <div className="bg-white rounded-md shadow-sm border border-slate-200 overflow-hidden" id="programs-table-wrapper">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" id="programs-data-table">
            <thead>
              <tr className="bg-slate-50/75 border-b border-slate-200 text-slate-400 text-[9px] font-black uppercase tracking-widest select-none">
                <th 
                  id="th-name"
                  className="px-6 py-4 cursor-pointer hover:bg-slate-100/50 hover:text-slate-900 transition-colors"
                  onClick={() => handleSort('name')}
                >
                  Program Name
                  <SortIndicator field="name" />
                </th>
                <th 
                  id="th-category"
                  className="px-6 py-4 cursor-pointer hover:bg-slate-100/50 hover:text-slate-900 transition-colors"
                  onClick={() => handleSort('category')}
                >
                  Category
                  <SortIndicator field="category" />
                </th>
                <th 
                  id="th-status"
                  className="px-6 py-4 cursor-pointer hover:bg-slate-100/50 hover:text-slate-900 transition-colors"
                  onClick={() => handleSort('status')}
                >
                  Status
                  <SortIndicator field="status" />
                </th>
                <th 
                  id="th-engagements"
                  className="px-6 py-4 cursor-pointer hover:bg-slate-100/50 hover:text-slate-900 transition-colors"
                  onClick={() => handleSort('engagements')}
                >
                  Engagements
                  <SortIndicator field="engagements" />
                </th>
                <th id="th-actions" className="px-6 py-4 text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700 text-[11px]" id="programs-table-body">
              {isLoading ? (
                [1, 2, 3, 4, 5].map(idx => (
                  <tr key={idx} className="animate-pulse" id={`skeleton-row-${idx}`}>
                    <td className="px-6 py-4"><div className="h-4 bg-slate-100 rounded w-1/2"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-slate-100 rounded w-1/3"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-slate-100 rounded w-16"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-slate-100 rounded w-12"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-slate-100 rounded w-16 mx-auto"></div></td>
                  </tr>
                ))
              ) : filteredPrograms.length === 0 ? (
                <tr id="empty-state-row">
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-400 uppercase font-black tracking-wider bg-slate-50/20">
                    No matching records found.
                  </td>
                </tr>
              ) : (
                filteredPrograms.map((program: any) => (
                  <tr 
                    key={program.id} 
                    className="hover:bg-slate-50/50 transition-colors group"
                    id={`program-row-${program.id}`}
                  >
                    <td className="px-6 py-4 font-bold max-w-sm">
                      <span 
                        id={`program-link-${program.id}`}
                        onClick={() => navigate(`/programs/${program.id}`)}
                        className="text-slate-900 hover:text-blue-600 cursor-pointer text-xs font-black uppercase tracking-tight hover:underline transition-colors block"
                      >
                        {program.name}
                      </span>
                      {program.description && (
                        <p className="text-[10px] text-slate-400 font-medium normal-case line-clamp-1 mt-0.5" id={`program-desc-${program.id}`}>
                          {program.description}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 font-bold text-slate-600 uppercase">
                      {program.program_categories?.label || (
                        <span className="text-slate-300 font-normal">Uncategorized</span>
                      )}
                    </td>
                    <td className="px-6 py-4 font-bold">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest border ${
                        program.is_active 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                          : 'bg-slate-100 text-slate-400 border-slate-200'
                      }`}>
                        {program.is_active ? 'Active' : 'Archived'}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-bold text-slate-600">
                      <div className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-200 px-2 py-1 rounded text-[10px]" id={`engagement-badge-${program.id}`}>
                        <Activity size={12} className="text-blue-500" />
                        <span>{program.engagements?.length || 0}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-2" id={`actions-container-${program.id}`}>
                        <button 
                          id={`edit-btn-${program.id}`}
                          onClick={() => setEditingProgram(program)}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                          title="Edit Program"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button 
                          id={`delete-btn-${program.id}`}
                          onClick={() => handleDelete(program.id, program.name)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-all"
                          title="Delete Program"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isAddProgramOpen && (
        <ProgramFormModal onClose={() => setIsAddProgramOpen(false)} />
      )}

      {editingProgram && (
        <ProgramFormModal 
          program={editingProgram} 
          onClose={() => setEditingProgram(null)} 
        />
      )}
    </div>
  );
};

export default ProgramsList;
